import { Injectable, signal } from '@angular/core';

const WELCOME_POPUP_SHOWN_KEY = 'welcomePopupShown';

/**
 * Service to track whether the welcome popup has been shown in the current session.
 * Ensures the popup appears only once per login; resets on logout.
 */
@Injectable({
  providedIn: 'root',
})
export class WelcomePopupStateService {
  /** True if the welcome popup has been shown at least once in this browser session. */
  readonly hasShownWelcomePopup = signal(this.readPersistedState());

  /** Mark the welcome popup as shown for this browser session. */
  markWelcomePopupShown(): void {
    this.hasShownWelcomePopup.set(true);
    this.persistState(true);
  }

  /** Reset state on logout so popup will show again on next login. */
  resetOnLogout(): void {
    this.hasShownWelcomePopup.set(false);
    this.persistState(false);
  }

  /** Reads initial shown-state from sessionStorage so refreshes keep the same behavior. */
  private readPersistedState(): boolean {
    try {
      return sessionStorage.getItem(WELCOME_POPUP_SHOWN_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /** Keeps sessionStorage in sync with the in-memory signal state. */
  private persistState(isShown: boolean): void {
    try {
      if (isShown) {
        sessionStorage.setItem(WELCOME_POPUP_SHOWN_KEY, 'true');
        return;
      }

      sessionStorage.removeItem(WELCOME_POPUP_SHOWN_KEY);
    } catch {
      // Ignore storage access errors; signal state still controls current runtime behavior.
    }
  }
}
